import * as preact from "preact";

import * as languageMap from "shared/languageMap";
import { userProfilePath } from "shared/usernameValidation";
import * as utils from "shared/utils";

interface BookCoverProps {
  _id: string;
  author?: string;
  language?: string;
  userBook?: {
    tagHash: {
      completed?: boolean;
    };
  };
  category?: string;
  showVotes?: boolean;
  upVoted?: boolean;
  totalVotesString?: string;
  youTubeID?: string;
  url?: string;
  urlDomain?: string;
  titleHint?: string;
  shortTitle?: string;
  chapterCount?: number; // XXX Should we remove this??
  metrics?: {
    vocabCEF?: boolean;
    CEF?: string;
    difficultyString?: string;
    words?: number;
    characters?: number;
  };
  haveFrequencies?: boolean;
  totalWordsString?: string;
  groupMode?: boolean;
  description?: string;
  status?: string;
  ownerID?: any;
  readToPercentage?: number;
  public?: boolean;
  compactMode: boolean; // If true, forces the compact mode like mobile, even on desktop

  clickHeart: () => void;
  clickReadLater: () => void;
}

/** The check beside a completed text's progress bar (My Texts' too) */
export const completedCheckIcon = () => (
  <svg
    width="13"
    height="11"
    viewBox="0 0 13 11"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M1.99985 5.99575L4.50233 8.49799L11.5005 1.5"
      stroke="currentColor"
      stroke-width="3"
      stroke-linecap="round"
    />
  </svg>
);

const Attribution: preact.FunctionComponent<Partial<BookCoverProps>> = ({
  status,
  author,
  url,
  urlDomain,
  ownerID,
}) => {
  if (status === "notAvailable") {
    return <span>N/A</span>;
  }

  const sections = [];

  if (url) {
    sections.push(<span title={url}>{urlDomain}</span>);
  } else if (author) {
    sections.push(<span title="Author">{author}</span>);
  }

  if (ownerID && ownerID.username) {
    sections.push(
      <span>
        {author ? "via " : ""}
        <span class="mobileOnly">{ownerID.username}</span>
        <a
          class="noMobile"
          href={userProfilePath(ownerID.username, ownerID._id)}
        >
          {ownerID.username}
        </a>
      </span>
    );
  }

  return (
    <span>
      {sections.map((section) => (
        <span>{section}, </span>
      ))}
    </span>
  );
};

const BookCoverView: preact.FunctionComponent<BookCoverProps> = ({
  _id,
  userBook,
  category,
  upVoted = false,
  totalVotesString = "",
  youTubeID,
  url,
  titleHint = "",
  shortTitle = "",
  chapterCount,
  metrics = {},
  haveFrequencies = false,
  totalWordsString = "",
  groupMode = false,
  description = "",
  status,
  ownerID,
  author,
  urlDomain,
  language,
  readToPercentage,
  public: publicBook,
  compactMode,

  clickHeart,
  clickReadLater,
}) => {
  const showCharacters = languageMap.usesCharacterCount(language);
  const unitLabel = showCharacters ? "characters" : "words";
  const lengthValue = showCharacters ? metrics.characters : metrics.words;
  const youTubeThumbnail = youTubeID ? (
    <div class="thumbContainer">
      <img
        class={`youTubeThumb${compactMode ? " compactMode" : ""}`}
        src={`//img.youtube.com/vi/${youTubeID}/mqdefault.jpg`}
      />
    </div>
  ) : null;

  return (
    <div class="libraryDocumentContainer">
      <a
        href={`/library/${_id}`}
        class={`libraryDocument ${category ? `category-${category}` : ""}`}
        data-documentID={_id}
        data-testid="library-document-open-link"
        id={`document-${_id}-open`}
      >
        <div class={`leftHandSide${compactMode ? " compactMode" : ""}`}>
          {youTubeThumbnail}
          <div class={`categoryContainer${compactMode ? " compactMode" : ""}`}>
            <span class={`category${compactMode ? " compactMode" : ""}`}>
              {category?.length > 0 ? category : "Other"}
            </span>
          </div>
        </div>

        <div class="mainInfo">
          {youTubeThumbnail}
          <div class="mainInfoRow">
            <h4 class={compactMode ? "compactMode" : ""}>
              {!youTubeID && url ? (
                <span>
                  <i style={{ fontSize: 20 }} class="icon-globe"></i>{" "}
                </span>
              ) : null}
              <span title={titleHint}>
                {shortTitle}
                {chapterCount && <small>({chapterCount} parts)</small>}
              </span>
            </h4>
            {!groupMode && publicBook ? (
              <div class="voteControlPlaceholder"></div>
            ) : null}
          </div>
          <div class={`attribution${compactMode ? " compactMode" : ""}`}>
            <Attribution
              status={status}
              author={author}
              url={url}
              urlDomain={urlDomain}
              ownerID={ownerID}
            />
            {!groupMode ? (
              <>
                {metrics.vocabCEF && (
                  <span
                    title={`${metrics.CEF} - ${
                      metrics.difficultyString
                    } - Rough estimate of the CEFR level required based on the ${
                      haveFrequencies ? "vocabulary and " : ""
                    }complexity of the text.`}
                  >
                    {
                      {
                        A1: "Beginner (A1)",
                        A2: "Beginner (A2)",
                        B1: "Intermediate (B1)",
                        B2: "Intermediate (B2)",
                        C1: "Advanced (C1)",
                        C2: "Advanced (C2)",
                      }[metrics.CEF]
                    }
                    ,{" "}
                  </span>
                )}
                {lengthValue ? (
                  <span title={`Number of ${unitLabel} in this text.`}>
                    <span>
                      {utils.addCommasToNumber(lengthValue)} {unitLabel}
                    </span>
                  </span>
                ) : null}
              </>
            ) : null}

            {groupMode && (
              <div class="description" style={{ clear: "both" }}>
                <small>{description}</small>
              </div>
            )}
          </div>

          <div class="mainInfoRow">
            <div class="progressBarContainer">
              <div class="progressBar">
                <div class="uncompletedBar categoryColor" />
                <div
                  class={`completedBar categoryColor`}
                  style={{ width: `${readToPercentage ?? 0}%` }}
                ></div>
              </div>
              {userBook?.tagHash.completed && (
                <div class="completedCheck categoryColor">
                  {completedCheckIcon()}
                </div>
              )}
            </div>

            {!userBook?.tagHash["queue"] && !userBook?.tagHash["completed"] ? (
              <div class="readLaterContainerPlaceholder">
                <button class="clickable" data-tag="queue">
                  <i class="icon-plus"></i>&nbsp; Read later
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </a>
      {!groupMode && publicBook ? (
        <div class="voteControl">
          <span
            class={`currentVote ${upVoted ? "upVoted" : ""}`}
            title="Total votes"
          >
            {totalVotesString}
          </span>
          <div
            class="upVoteButton clickable"
            onClick={(event) => {
              console.log("click heart");
              event.preventDefault();
              event.stopPropagation();
              clickHeart();
            }}
          >
            <i
              id={`document-${_id}-upVote`}
              class={`upVote ${upVoted ? "upVoted" : ""} icon-heart${
                !upVoted ? "-empty" : ""
              }`}
              title="Like this text"
            ></i>
          </div>
        </div>
      ) : null}
      <div class="readLaterContainer">
        {!userBook?.tagHash["queue"] && !userBook?.tagHash["completed"] ? (
          <button
            class="clickable"
            data-tag="queue"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              clickReadLater();
            }}
          >
            <i class="icon-plus"></i>&nbsp; Read later
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default BookCoverView;
